import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Sparkles } from 'lucide-react';
import { FollowButton } from './FollowButton';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface Creator {
  id: string;
  display_name: string;
  avatar_url: string;
  username: string;
  is_verified: boolean;
  bio: string;
}

export const SuggestedCreators = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSuggestedCreators();
  }, [user]);

  const loadSuggestedCreators = async () => {
    if (!user) return;

    // Get creators that the user is NOT following
    const { data: following } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', user.id);

    const followingIds = following?.map(f => f.following_id) || [];
    
    // Get creators (users with creator role)
    const { data: creatorRoles } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('role', 'creator');

    const creatorIds = creatorRoles?.map(r => r.user_id) || [];

    // Get profiles of creators not being followed
    const { data, error } = await supabase
      .from('profiles')
      .select('id, display_name, avatar_url, username, is_verified, bio')
      .in('id', creatorIds)
      .not('id', 'in', `(${[user.id, ...followingIds].join(',')})`)
      .limit(5);

    if (!error && data) {
      setCreators(data);
    }
    setLoading(false);
  };

  if (loading || creators.length === 0) {
    return null;
  }

  return (
    <Card className="clean-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Suggested Creators
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {creators.map((creator) => (
            <div
              key={creator.id}
              className="flex items-center gap-3 p-3 border rounded-xl bg-white hover-lift cursor-pointer"
              onClick={() => navigate(`/user/${creator.id}`)}
            >
              <Avatar className="border-2 border-primary/10">
                <AvatarImage src={creator.avatar_url} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {creator.display_name?.[0] || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground flex items-center gap-1">
                  {creator.display_name}
                  {creator.is_verified && (
                    <Badge variant="secondary" className="text-xs">✓</Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {creator.bio || `@${creator.username}`}
                </p>
              </div>
              <FollowButton userId={creator.id} size="sm" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
